/**
 * Shared Configuration
 * Centralized configuration management following React/TypeScript best practices
 * Now using the proper environment configuration
 */

import { ENV as CONFIG_ENV } from '@/config/environment';

// Re-export environment configuration for backward compatibility
export const ENV = {
  NODE_ENV: CONFIG_ENV.app.environment,
  VITE_API_URL: CONFIG_ENV.api.baseUrl,
  VITE_ANALYTICS_ID: '', // Add to env file if needed
  VITE_SENTRY_DSN: '', // Add to env file if needed
  IS_DEV: CONFIG_ENV.app.isDevelopment,
  IS_PROD: CONFIG_ENV.app.isProduction,
} as const;

// API Configuration - now uses ENV variables properly
export const API_CONFIG = {
  baseURL: CONFIG_ENV.api.baseUrl,
  timeout: CONFIG_ENV.api.timeout,
  retries: CONFIG_ENV.api.retries,
  retryDelay: 1000,
  projectId: CONFIG_ENV.project.id, // Project ID for headers
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Project-Id': CONFIG_ENV.project.id, // Required header for all requests
  },
} as const;

// App Configuration - now uses ENV variables
export const APP_CONFIG = {
  name: CONFIG_ENV.app.name,
  description: CONFIG_ENV.app.description,
  version: CONFIG_ENV.app.version,
  author: 'MASTAQ Consulting',
  homepage: 'https://aditas.com',
  supportEmail: 'support@aditas.com',
  projectId: CONFIG_ENV.project.id,
  projectName: CONFIG_ENV.project.name,
} as const;

// Route Configuration
export const ROUTES = {
  HOME: '/',
  ABOUT: '/about',
  PROJECTS: '/projects',
  PROJECT_DETAIL: (id: string) => `/projects/${id}`,
  BLOG: '/blog',
  BLOG_POST: (id: string) => `/blog/${id}`,
  BLOG_PREVIEW: (id: string) => `/blog/preview/${id}`,
  BLOG_CREATE: '/blog/create',
  BLOG_EDIT: (id: string) => `/blog/edit/${id}`,
  DRAFTS: '/drafts',
  LOGIN: '/login',
  REGISTER: '/register',
  LOGOUT: '/logout',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  DASHBOARD: '/dashboard',
  DASHBOARD_SECTION: (section: string) => `/dashboard/${section}`,
  CALCULATOR: '/calculator',
  DEV_DEBUG: '/dev/debug',
  NOT_FOUND: '/404',
  SETTINGS: '/settings',
  PROFILE: '/profile',
  TOASTMASTERS_SUITE: '/toastmaster',
  
} as const;

export const ADMIN_ROUTES = {
  DASHBOARD: '/admin/dashboard',
  USERS: '/admin/users',
  BLOGS: '/admin/blogs',
  ANALYTICS: '/admin/analytics',
  PORTFOLIO: '/admin/portfolio',
  FLASHCARDS: '/admin/flashcards',
  ENTITLEMENTS: '/admin/entitlements',
} as const;

// Storage Keys
export const SHARED_STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER_DATA: 'user_data',
  THEME: 'theme',
  LANGUAGE: 'language',
  PREFERENCES: 'preferences',
} as const;

// Theme Configuration
export const THEME_CONFIG = {
  default: 'light',
  available: ['light', 'dark', 'system'] as const,
  storageKey: SHARED_STORAGE_KEYS.THEME,
} as const;

// Logging Configuration
export const LOG_CONFIG = {
  level: ENV.IS_PROD ? 'error' : 'debug',
  enableConsole: !ENV.IS_PROD,
  enableRemote: ENV.IS_PROD,
  maxEntries: 1000,
} as const;

// Security Configuration
export const SECURITY_CONFIG = {
  tokenExpiryTime: 24 * 60 * 60 * 1000, // 24 hours
  refreshTokenExpiryTime: 7 * 24 * 60 * 60 * 1000, // 7 days
  maxLoginAttempts: 5,
  lockoutDuration: 30 * 60 * 1000, // 30 minutes
} as const;

// Validation Configuration
export const VALIDATION_CONFIG = {
  password: {
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
  },
  email: {
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  },
  username: {
    minLength: 3,
    maxLength: 20,
    pattern: /^[a-zA-Z0-9_]+$/,
  },
} as const;

// UI Configuration
export const UI_CONFIG = {
  pagination: {
    defaultPageSize: 10,
    pageSizeOptions: [10, 25, 50, 100],
  },
  animation: {
    duration: 300,
    easing: 'ease-in-out',
  },
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
} as const;

// Export all configurations
export default {
  ENV,
  API_CONFIG,
  APP_CONFIG,
  ROUTES,
  SHARED_STORAGE_KEYS,
  THEME_CONFIG,
  LOG_CONFIG,
  SECURITY_CONFIG,
  VALIDATION_CONFIG,
  UI_CONFIG,
} as const;

// Re-export as STORAGE_KEYS for backward compatibility
export const STORAGE_KEYS = SHARED_STORAGE_KEYS;
